import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { clearQueryCache } from '@globalconnect/query';
import {
  DropdownInputChangedEvent,
  DropdownOption,
  GcButtonComponent,
  GcDropdownInputComponent,
  GcInputFieldComponent,
  GcListItem,
  GcLoadingSpinnerComponent,
  IpoDropdownComponent,
  IpoLetDirectiveModule,
  IpoTranslatePipe,
} from '@ip-only/front-end-library';
import { fadeAnimation } from '@shared/animations/enter-leave-animations';
import { Address } from '@shared/api';
import { PageComponent } from '@shared/layout/page/page.component';
import { provideTranslations } from '@shared/utils/translations-util';
import { BehaviorSubject, Subscription, debounce, timer } from 'rxjs';
import { AddressSearchApiService } from '../../api/address-search-api.service';
import { AddressSearchResponse } from '../../api/entities/address-search';
import { OpportunitiesForAddressResponse } from '../../api/entities/opportunity-address-suggestions';
import { OpportunityDetails } from '../../api/entities/opportunity-details';
import { addressSearchPageTranslations } from '../../lang/address-search-page.sv';

type SearchMode = 'ADDRESS' | 'ACCESS_ID';

@Component({
  selector: 'app-address-search',
  imports: [
    CommonModule,
    RouterModule,
    IpoTranslatePipe,
    IpoLetDirectiveModule,
    IpoDropdownComponent,
    GcInputFieldComponent,
    PageComponent,
    GcLoadingSpinnerComponent,
    GcButtonComponent,
    GcDropdownInputComponent,
    GcInputFieldComponent,
  ],
  templateUrl: './address-search.component.html',
  animations: [fadeAnimation],
  styleUrls: ['./address-search.component.scss'],
  providers: [
    provideTranslations({
      sv: {
        ...addressSearchPageTranslations,
      },
    }),
  ],
})
export class AddressSearchComponent implements OnInit, OnDestroy {
  public searchMode: SearchMode = 'ADDRESS';
  public redirectUrls = {
    orderFiber: 'beställ/fiber/',
    orderService: 'beställ/tjänster/',
    genericServiceSupply: 'beställ/tjänster/utbud',
    updateContactInformation: '',
    noticeOfInterest: '',
  };
  public savedSearchQuery: string;
  public searchQuery$ = new BehaviorSubject<string>('');
  public selectedOpportunity?: DropdownOption<string>;

  public suggestions$ = new BehaviorSubject<GcListItem[] | null>([]);
  public opportunities$ = new BehaviorSubject<DropdownOption<string>[] | null>(
    null
  );
  public selectedOpportunityDetails$ =
    new BehaviorSubject<OpportunityDetails | null>(null);

  public subscriptions: Subscription[] = [];
  public addressSearchState$ =
    this.addressSearchApiService.getAddressSearchSuggestions.getState$();
  public opportunitiesState$ =
    this.addressSearchApiService.getOpportunitiesForAddress.getState$();
  public opportunityDetailsState$ =
    this.addressSearchApiService.getOpportunityDetailsById.getState$();

  private ignoreState = true;
  isLoadingDuringDebounce = false;
  selectedAddress: GcListItem | null = null;

  constructor(
    private addressSearchApiService: AddressSearchApiService,
    private router: Router
  ) {}

  ngOnInit(): void {
    clearQueryCache(); // Clear all cached data
    this.subscriptions.push(
      this.searchQuery$.pipe(debounce(() => timer(1000))).subscribe((query) => {
        if (this.searchMode === 'ADDRESS') {
          this.addressSearchChangeHandler(query);
        }
      })
    );

    this.subscriptions.push(
      this.addressSearchState$.subscribe((state) => {
        if (state && state.data && !this.ignoreState) {
          this.isLoadingDuringDebounce = false;
          this.updateAddressSearchSuggestions(state.data);
        }
      })
    );

    this.subscriptions.push(
      this.opportunitiesState$.subscribe((state) => {
        if (state.isSuccess && state.data && !this.ignoreState) {
          this.updateOpportunitiesForAddress(state.data);
        }
      })
    );

    this.subscriptions.push(
      this.opportunityDetailsState$.subscribe((state) => {
        if (state.isSuccess && state.data && !this.ignoreState) {
          this.selectedOpportunityDetails$.next(state.data);
        }
      })
    );

    this.ignoreState = false;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  public addressSearchChangeHandler(query: string) {
    if (query.length > 2) {
      this.onResetSearch();
      this.addressSearchApiService.getAddressSearchSuggestions.doRequest(query);
    }
  }

  onAddressSelected(magicKey: string) {
    const selectedItem = this.suggestions$
      .getValue()
      ?.find((item) => item.id === magicKey);
    if (selectedItem) {
      this.onResetSearch();
      this.savedSearchQuery = selectedItem.label;
      this.selectedAddress = selectedItem;
      this.addressSearchApiService.getOpportunitiesForAddress.doRequest(
        selectedItem.label,
        selectedItem.id
      );
    }
  }

  public opportunitySelectHandler(option: DropdownOption<string>) {
    this.selectedOpportunityDetails$.next(null);
    this.getOpportunityDetails(option.value);
    this.selectedOpportunity = option;
  }

  public onResetSearch() {
    this.suggestions$.next(null);
    this.opportunities$.next(null);
    this.selectedOpportunity = undefined;
    this.selectedOpportunityDetails$.next(null);
  }

  public toggleSearchMode(mode: SearchMode) {
    this.onResetSearch();
    this.searchMode = mode;
  }

  public redirect(url: string): void {
    this.router.navigateByUrl(url);
  }

  private updateAddressSearchSuggestions(data: AddressSearchResponse) {
    const suggestions = this.mapAddressSearchResponseToListItems(data);
    this.suggestions$.next(suggestions);
  }

  private mapAddressSearchResponseToListItems(
    data: AddressSearchResponse
  ): GcListItem[] {
    return data.items.map((value) => ({
      label: value.address,
      id: value.magicKey,
    }));
  }

  onAddressInputChanged(event: DropdownInputChangedEvent) {
    const searchString = event.target.value;
    this.isLoadingDuringDebounce = true;
    this.searchQuery$.next(searchString);
    this.selectedAddress = null;
  }

  getActiveAddressId() {
    return this.selectedAddress ? this.selectedAddress.id : undefined;
  }

  getIsAddressLoading(
    isLoadingDuringDebounce: boolean,
    isQueryStateLoading: boolean
  ): boolean {
    return isLoadingDuringDebounce || isQueryStateLoading;
  }

  onAccessInputChange(accessQuery: string) {
    this.searchQuery$.next(accessQuery);
  }

  onAccessSearchClick(): void {
    const queryValue = this.searchQuery$.getValue().trim();
    if (queryValue) {
      this.router.navigate([
        '../' + this.redirectUrls.orderService,
        queryValue,
      ]);
    }
  }

  private updateOpportunitiesForAddress(data: OpportunitiesForAddressResponse) {
    this.selectedOpportunityDetails$.next(null);
    // If MDU, multiple opportunies can exist at the same address

    const opportunities = data.items.map((address: Address) => {
      return {
        label:
          ((address.apartmentNumber ?? address.altApartmentNumber) as string) ??
          '',
        value: address.opportunityId,
      };
    });

    this.opportunities$.next(opportunities);

    if (data.items.length === 1) {
      this.getOpportunityDetails(data.items[0].opportunityId);
    }
  }

  private getOpportunityDetails(opportunityId: string) {
    this.addressSearchApiService.getOpportunityDetailsById.doRequest(
      opportunityId
    );
  }
}
